import { useReducer, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, ApiError } from '@/basedata/apiCall'
import { getStoredEventSlug } from '@/helpers/eventSlug'
import { markOnboardingGuestEnrolled } from '@/helpers/onboardingGuest'
import { getSelfieConfig } from '@/helpers/selfieConfig'
import type { LivenessErrorType, SelfiePhase } from '@/types/liveness'
import { MAX_SELFIE_ATTEMPTS } from '@/types/liveness'
import type { CameraRestartInfo } from '@/Pages/Selfie/helpers/cameraAccess'
import {
  describeCameraRestart,
  describeLivenessError,
  livenessErrorDetails,
} from '@/Pages/Selfie/helpers/captureLogPayload'
import { resolveLivenessError } from '@/Pages/Selfie/helpers/resolveLivenessError'
import { isPlaceholderSessionId } from '@/Pages/Selfie/helpers/isPlaceholderSessionId'
import { successNavigationOptions } from '@/Pages/Success/types/successLocationState'

const LIVENESS_TEARDOWN_MS = 800
const SUPPORT_URL = 'https://support.example.com'
export type UseSelfieResult = {
  phase: SelfiePhase
  isTearingDown: boolean
  handleCameraReady: () => void
  handleAnalysisComplete: (base64Image: string) => void
  handleError: (error: unknown) => void
  handleCameraRestart: (info: CameraRestartInfo) => void
  handleRetry: () => void
  handleCancel: () => void
  handleContactSupport: () => void
}

type State = {
  phase: SelfiePhase
  attempts: number
}

type Action =
  | { type: 'CAMERA_READY' }
  | { type: 'ANALYSIS_COMPLETE' }
  | { type: 'SUBMIT_FAILED' }
  | { type: 'ERROR'; errorType: LivenessErrorType }
  | { type: 'RETRY' }

const initialState: State = {
  phase: { phase: 'capturing', cameraReady: false },
  attempts: 0,
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'CAMERA_READY':
      if (state.phase.phase !== 'capturing') return state
      return { ...state, phase: { phase: 'capturing', cameraReady: true } }

    case 'ANALYSIS_COMPLETE':
      if (state.phase.phase !== 'capturing') return state
      return { ...state, phase: { phase: 'processing' } }

    case 'SUBMIT_FAILED':
      return { ...state, phase: { phase: 'capturing', cameraReady: false } }

    case 'ERROR': {
      const attempts = state.attempts + 1
      return { ...state, phase: { phase: 'help', errorType: action.errorType, attempts }, attempts }
    }

    case 'RETRY':
      if (state.phase.phase !== 'help') return state
      if (state.attempts >= MAX_SELFIE_ATTEMPTS) return state
      return { ...state, phase: { phase: 'capturing', cameraReady: false } }
  }
}

type UseSelfieOptions = {
  /** Sessão do ONBOARDING (aceite de consentimento) — chave do funil; nunca muda aqui. */
  sessionId: string
  /** Sessão AWS Face Liveness (modo liveness); null em modo simple/mock. */
  livenessSessionId: string | null
  onLivenessSessionIdChange: (livenessSessionId: string) => void
}

export function useSelfie({
  sessionId,
  livenessSessionId,
  onLivenessSessionIdChange,
}: UseSelfieOptions): UseSelfieResult {
  const [state, dispatch] = useReducer(reducer, initialState)
  const [isTearingDown, setIsTearingDown] = useState(false)
  const navigate = useNavigate()

  const handleCameraReady = () => {
    dispatch({ type: 'CAMERA_READY' })
  }

  const handleAnalysisComplete = (base64Image: string) => {
    dispatch({ type: 'ANALYSIS_COMPLETE' })
    // Modo evento: o eventSlug orienta o BFF a resolver o captureType do tenant
    // dono do evento (a decisão continua server-side).
    const eventSlug = getStoredEventSlug()
    void api.selfie
      .submit({
        base64Image,
        sessionId,
        ...(livenessSessionId !== null ? { livenessSessionId } : {}),
        ...(eventSlug !== null ? { eventSlug } : {}),
      })
      .then(() => {
        navigate('/selfie/validating', { state: { sessionId } })
      })
      .catch((error: unknown) => {
        dispatch({ type: 'SUBMIT_FAILED' })
        // 409: o service recusou reindexar a face porque este convidado já
        // concluiu o cadastro. Não é erro para quem está na tela — é o fim do
        // funil: sela o estado e mostra o comprovante.
        if (error instanceof ApiError && error.status === 409) {
          markOnboardingGuestEnrolled(null)
          navigate('/success', successNavigationOptions)
          return
        }
        navigate('/error', { replace: true, state: { errorType: 'generic' } })
      })
  }

  const handleError = (error: unknown) => {
    const errorType = resolveLivenessError(error)

    // Rastro para a modal de logs do painel: quem atende o convidado precisa
    // saber SE foi câmera, iluminação ou vivacidade. Fire-and-forget de
    // propósito — a tela de ajuda não espera o log nem quebra se ele falhar.
    void api.selfie
      .captureLog({
        sessionId,
        code: errorType,
        message: describeLivenessError(errorType),
        details: livenessErrorDetails(error),
      })
      .catch(() => {
        /* log é observabilidade: falha aqui não muda o que o convidado vê */
      })

    setIsTearingDown(true)
    window.setTimeout(() => {
      dispatch({ type: 'ERROR', errorType })
      setIsTearingDown(false)
    }, LIVENESS_TEARDOWN_MS)
  }

  const handleCameraRestart = (info: CameraRestartInfo) => {
    // Mesmo fire-and-forget do handleError: a reabertura da câmera não espera
    // o log. O `keepalive` do captureLog faz ele sobreviver à recarga da página.
    void api.selfie
      .captureLog({
        sessionId,
        code: info.reloading ? 'camera_reload' : 'camera_restart',
        message: describeCameraRestart(info),
        details: {
          reason: info.reason,
          attempt: info.attempt,
          ...(info.errorName !== null ? { errorName: info.errorName } : {}),
        },
      })
      .catch(() => {
        /* log é observabilidade: falha aqui não muda o que o convidado vê */
      })
  }

  const handleRetry = () => {
    if (getSelfieConfig().mockLiveness) {
      dispatch({ type: 'RETRY' })
      return
    }

    void api.selfie
      .createSession({ sessionId })
      .then(({ sessionId: newLivenessSessionId }) => {
        if (isPlaceholderSessionId(newLivenessSessionId)) {
          dispatch({ type: 'ERROR', errorType: 'generic_liveness' })
          return
        }
        onLivenessSessionIdChange(newLivenessSessionId)
        dispatch({ type: 'RETRY' })
      })
      .catch(() => {
        dispatch({ type: 'ERROR', errorType: 'generic_liveness' })
      })
  }

  const handleCancel = () => {
    // Let Amplify release its event streams before the page unmounts.
    window.setTimeout(() => navigate(-1), 150)
  }

  const handleContactSupport = () => {
    window.open(SUPPORT_URL, '_blank', 'noopener,noreferrer')
  }

  return {
    phase: state.phase,
    isTearingDown,
    handleCameraReady,
    handleAnalysisComplete,
    handleError,
    handleCameraRestart,
    handleRetry,
    handleCancel,
    handleContactSupport,
  }
}
